import type { ItemType, RewardBundle, SeasonInfo } from '@vm/shared';
import { contentItem } from './catalog';

/**
 * Battle pass seasons. The free track pays coins, a few gems, coin-shop cosmetics and some
 * pass-exclusive cosmetics; the premium track (bought with gems) pays cosmetics and gems, and may
 * carry anything else. The rule that keeps it fair (`seasonIssues`): the premium track alone pays
 * back at least its price in gems, so a player who finishes one pass can buy the next with it.
 */
const TIERS = 50;
const XP_PER_TIER = 2000;
const PREMIUM_PRICE_GEMS = 800;

const item = (type: ItemType, id: string): RewardBundle => ({ items: [{ type, id }] });

const S1_FREE_ITEMS: Record<number, RewardBundle> = {
  5: item('armor', 'urban'),
  8: item('petstyle', 'camo'),
  10: item('camo', 'hazard'),
  15: item('armor', 'desert'),
  25: item('armor', 'scout'),
  30: item('deathfx', 'slime_bubbles'),
  35: item('skin', 'engineer'),
  45: item('banner', 'gamer'),
  50: item('skin', 'ninja'),
};

const S1_PREMIUM_ITEMS: Record<number, RewardBundle> = {
  1: item('armor', 'crimson'),
  6: item('skin', 'robot'),
  10: item('camo', 'aurora'),
  12: item('armor', 'cobalt'),
  16: item('banner', 'starlight'),
  18: item('deathfx', 'confetti'),
  22: item('camo', 'chrome'),
  24: item('armor', 'toxic'),
  28: item('deathfx', 'neon_glitch'),
  30: item('skin', 'pirate'),
  34: item('petstyle', 'neon'),
  36: item('armor', 'jungle'),
  40: item('camo', 'neon_grid'),
  42: item('armor', 'neon'),
  46: item('skin', 'astronaut'),
  // The top tier tops the premium gems up to the pass price (15/30 a tier pay 600 of the 800).
  50: { ...item('armor', 'void'), gems: 200 },
};

function freeReward(tier: number): RewardBundle | null {
  if (S1_FREE_ITEMS[tier]) return S1_FREE_ITEMS[tier];
  if (tier % 10 === 0) return { gems: 15 };
  if (tier % 2 === 1) return { coins: Math.round((150 + 10 * tier) / 50) * 50 };
  return null;
}

function premiumReward(tier: number): RewardBundle | null {
  if (S1_PREMIUM_ITEMS[tier]) return S1_PREMIUM_ITEMS[tier];
  return { gems: tier % 5 === 0 ? 30 : 15 };
}

export const SEASON_1: SeasonInfo = {
  id: 's1',
  name: 'Season 1: Voxel Dawn',
  startsAt: '2026-09-01T00:00:00.000Z',
  endsAt: '2026-12-01T00:00:00.000Z',
  tiers: TIERS,
  xpPerTier: XP_PER_TIER,
  premiumPriceGems: PREMIUM_PRICE_GEMS,
  rewards: Array.from({ length: TIERS }, (_, i) => ({ tier: i + 1, free: freeReward(i + 1), premium: premiumReward(i + 1) })),
};

/** All seasons in chronological order. */
export const SEASONS: SeasonInfo[] = [SEASON_1];

/** The season running at `now`, or null between seasons. */
export function activeSeason(now: Date, seasons: readonly SeasonInfo[] = SEASONS): SeasonInfo | null {
  const t = now.getTime();
  return seasons.find((s) => Date.parse(s.startsAt) <= t && t < Date.parse(s.endsAt)) ?? null;
}

/** The active season, else the most recently started one, else the first upcoming one. */
export function currentSeason(now: Date, seasons: readonly SeasonInfo[] = SEASONS): SeasonInfo {
  const active = activeSeason(now, seasons);
  if (active) return active;
  const t = now.getTime();
  const started = seasons.filter((s) => Date.parse(s.startsAt) <= t);
  return started[started.length - 1] ?? seasons[0];
}

/** Highest tier unlocked with `xp` battle pass XP. Tier 1 is unlocked from the start. */
export function bpTier(season: Pick<SeasonInfo, 'tiers' | 'xpPerTier'>, xp: number): number {
  return Math.min(season.tiers, 1 + Math.floor(Math.max(0, xp) / season.xpPerTier));
}

export function seasonReward(season: SeasonInfo, tier: number, track: 'free' | 'premium'): RewardBundle | null {
  const row = season.rewards.find((r) => r.tier === tier);
  return row ? row[track] : null;
}

/** Gems the premium track pays over the whole season. */
export function premiumTrackGems(season: Pick<SeasonInfo, 'rewards' | 'tiers'>): number {
  return season.rewards.reduce((a, r) => a + (r.tier <= season.tiers ? (r.premium?.gems ?? 0) : 0), 0);
}

export interface SeasonIssue {
  /** The part of the season it is about (the admin console shows it next to that field). */
  field: 'name' | 'dates' | 'tiers' | 'xpPerTier' | 'price' | 'rewards';
  tier?: number;
  track?: 'free' | 'premium';
  message: string;
}

const MAX_REWARD_ITEMS = 8;

/**
 * What stops a season from being saved, in words staff can act on. Shared by the admin console
 * (shown while editing) and the API (checked on save), so both always agree.
 */
export function seasonIssues(s: SeasonInfo): SeasonIssue[] {
  const out: SeasonIssue[] = [];
  if (!s.name.trim()) out.push({ field: 'name', message: 'Give the season a name.' });
  else if (s.name.length > 64) out.push({ field: 'name', message: 'The name is longer than 64 characters.' });
  const start = Date.parse(s.startsAt);
  const end = Date.parse(s.endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end)) out.push({ field: 'dates', message: 'Set a start and an end date.' });
  else if (end <= start) out.push({ field: 'dates', message: 'The season has to end after it starts.' });
  if (!Number.isInteger(s.tiers) || s.tiers < 1 || s.tiers > 100) out.push({ field: 'tiers', message: 'A season has between 1 and 100 tiers.' });
  if (!Number.isInteger(s.xpPerTier) || s.xpPerTier < 100) out.push({ field: 'xpPerTier', message: 'A tier needs at least 100 pass XP.' });
  if (!Number.isInteger(s.premiumPriceGems) || s.premiumPriceGems < 0) out.push({ field: 'price', message: 'The premium price is a whole number of gems.' });
  const seen = new Set<number>();
  const past: number[] = [];
  for (const r of [...s.rewards].sort((a, b) => a.tier - b.tier)) {
    if (seen.has(r.tier)) out.push({ field: 'rewards', tier: r.tier, message: `Tier ${r.tier} is listed twice.` });
    seen.add(r.tier);
    if (r.tier > s.tiers && (r.free || r.premium)) {
      past.push(r.tier);
      continue;
    }
    for (const track of ['free', 'premium'] as const) {
      const items = r[track]?.items ?? [];
      if (items.length > MAX_REWARD_ITEMS) out.push({ field: 'rewards', tier: r.tier, track, message: `Tier ${r.tier} ${track} has more than ${MAX_REWARD_ITEMS} items.` });
      for (const it of items) {
        if (!contentItem(it.type, it.id)) out.push({ field: 'rewards', tier: r.tier, track, message: `Tier ${r.tier} ${track}: ${it.type} "${it.id}" isn't in the game.` });
      }
    }
  }
  if (past.length) {
    const which = past.length === 1 ? `Tier ${past[0]} has` : `Tiers ${past[0]}–${past[past.length - 1]} have`;
    out.push({ field: 'tiers', message: `${which} rewards, but the season only has ${s.tiers} tiers.` });
  }
  const gems = premiumTrackGems(s);
  if (gems < s.premiumPriceGems) {
    out.push({
      field: 'price',
      track: 'premium',
      message: `The premium track pays back ${gems} of its ${s.premiumPriceGems} gems. It has to pay back at least the price, so players can earn the next pass from this one.`,
    });
  }
  return out;
}

/** Problems between seasons (ids used twice, overlapping dates), on the merged list sorted by start. */
export function seasonListIssues(seasons: readonly SeasonInfo[]): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const s of seasons) {
    if (ids.has(s.id)) out.push(`Two seasons use the id "${s.id}".`);
    ids.add(s.id);
  }
  const sorted = [...seasons].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  for (let i = 1; i < sorted.length; i++) {
    if (Date.parse(sorted[i].startsAt) < Date.parse(sorted[i - 1].endsAt)) out.push(`${sorted[i].name} starts before ${sorted[i - 1].name} ends.`);
  }
  return out;
}
